import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import React from 'react'
import { Tag } from 'antd'
import { EmployeeListItem, ISorter } from './interfaces'
import { Value } from '../../../pages_v2/elements'
import { formatPhoneNumber } from '../../../../lib/utils/helpers'
import { RowActionMenu } from '../../../components/row-action-menu'
import {
  formatWithDisplayPattern,
  parseDisplayFormatValue,
} from '../../../components/input-text/mask-utils'
import { ComplianceListIcons } from './ui/compliance-list-icons'

const SNILS_FORMAT = '000-000-000 00'

const ColumnsGeneral: ColumnsType<EmployeeListItem> = [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    width: 80,
    sorter: true,
  },
  {
    title: 'ФИО',
    dataIndex: 'fio',
    key: 'fio',
    width: 200,
    sorter: true,
    render: (val, record) => (
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          minWidth: 0,
        }}
      >
        <span style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Value data={val} />
        </span>
        <ComplianceListIcons record={record} />
      </span>
    ),
  },
  {
    title: 'Телефон',
    dataIndex: 'phone',
    key: 'phone',
    width: 180,
    sorter: true,
    render: (_, record) => {
      const phones = record.phones ?? (record.phone ? [record.phone] : [])
      if (phones.length === 0) return null
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {phones.map((p, i) => (
            <span key={i}>{formatPhoneNumber(p)}</span>
          ))}
        </div>
      )
    },
  },
  {
    title: 'СНИЛС',
    dataIndex: 'snils',
    key: 'snils',
    width: 150,
    sorter: true,
    render: val => {
      if (val == null || val === '') return <Value data={val} />
      const digits = parseDisplayFormatValue(String(val), SNILS_FORMAT)
      const formatted = formatWithDisplayPattern(digits, SNILS_FORMAT)
      return <Value data={formatted} />
    },
  },
  {
    title: 'Дата рождения',
    dataIndex: 'date_birth',
    key: 'date_birth',
    width: 130,
    sorter: true,
    render: val => (val ? dayjs(val).format('DD.MM.YYYY') : <Value data={val} />),
  },
  {
    title: 'Должность',
    dataIndex: 'post',
    key: 'post',
    width: 180,
    sorter: true,
    render: val => <Value data={val} />,
  },
  {
    title: 'Отдел',
    dataIndex: 'department',
    key: 'department',
    width: 150,
    sorter: true,
    render: val => <Value data={val} />,
  },
  {
    title: 'Участок 1',
    dataIndex: 'section_1',
    key: 'section_1',
    width: 120,
    sorter: true,
    render: val => <Value data={val} />,
  },
  {
    title: 'Участок 2',
    dataIndex: 'section_2',
    key: 'section_2',
    width: 120,
    sorter: true,
    render: val => <Value data={val} />,
  },
  {
    title: 'Офис',
    dataIndex: 'office',
    key: 'office',
    width: 200,
    sorter: true,
    render: val => <Value data={val} />,
  },
  {
    title: 'Компания',
    dataIndex: 'company',
    key: 'company',
    width: 200,
    sorter: true,
    render: val => <Value data={val} />,
  },
  {
    title: 'Дата приема',
    dataIndex: 'employment_date',
    key: 'employment_date',
    width: 130,
    sorter: true,
    render: val => (val ? dayjs(val).format('DD.MM.YYYY') : <Value data={val} />),
  },
  {
    title: 'Дата увольнения',
    dataIndex: 'dismissal_event_date',
    key: 'dismissal_event_date',
    width: 150,
    sorter: true,
    render: val => (val ? dayjs(val).format('DD.MM.YYYY') : <Value data={val} />),
  },
  {
    title: 'Причина увольнения',
    dataIndex: 'last_dismissal_reason',
    key: 'last_dismissal_reason',
    width: 200,
    render: val => <Value data={val} />,
  },
  {
    title: 'Статус',
    dataIndex: 'status',
    key: 'status',
    width: 100,
    render: (_: string, record: EmployeeListItem) => {
      const status = record.status ?? (record.is_active ? 'active' : 'dismissed')
      if (status === 'maternity') {
        return <Tag color="orange">Декрет</Tag>
      }
      if (status === 'dismissed') {
        return <Tag color="red">Уволен</Tag>
      }
      return <Tag color="green">Активен</Tag>
    },
  },
  {
    title: 'Дата создания',
    width: 180,
    dataIndex: 'created_at',
    key: 'created_at',
    render: val => (val ? dayjs(val).format('DD.MM.YYYY HH:mm') : <Value data={val} />),
  },
  {
    title: 'Дата обновления',
    width: 180,
    dataIndex: 'updated_at',
    key: 'updated_at',
    render: val => (val ? dayjs(val).format('DD.MM.YYYY HH:mm') : <Value data={val} />),
  },
]

const ColumnAction: ColumnsType<EmployeeListItem> = [
  {
    title: '',
    key: 'operation',
    className: 'no-edit',
    fixed: 'right',
    width: 50,
    align: 'center',
    render: (_, { id, deleteRecord }) => {
      return (
        <span
          onClick={e => {
            e.stopPropagation()
          }}
        >
          <RowActionMenu id={Number(id)} deleteRecord={deleteRecord} />
        </span>
      )
    },
  },
]

export function getColumns(sorter: ISorter) {
  const columns: ColumnsType<EmployeeListItem> = []
  columns.push(...ColumnsGeneral)
  columns.push(...ColumnAction)
  return columns.map(item => {
    if (item.key === sorter.sort_field) {
      item.sortOrder = sorter.sort_order === 'ascend' ? 'ascend' : 'descend'
    } else {
      delete item.sortOrder
    }
    return item
  })
}
